<template>
  <div class="payment-option" :class="{ active: isSelected }">
    <div class="option-header">
      <base-form-field
        ref="fieldRef"
        v-model="selectedPaymentOption"
        @update:model-value="emit('update:model-value', $event)"
        :schema="schema.paymentOption"
        :options="[option]"
      />
      <h3>{{ option.repaymentTypeName }}</h3>
    </div>
    <div v-if="schoolSelected && requestedAmount && requestedAmount > 0" class="payment-amounts">
      <div class="payment in-school">
        <p>{{ option.monthlyInSchoolPaymentDisplay }}</p>
        <p>In-School Monthly Payments</p>
      </div>
      <div class="payment post-school">
        <p>{{ option.monthlyPostSchoolPaymentDisplay }}</p>
        <p>Post Graduation Monthly Payments</p>
      </div>
    </div>
    <div v-else>
      <p
        v-if="
          (!schoolSelected && requestedAmount && requestedAmount === 0) ||
          (!schoolSelected && !requestedAmount)
        "
      >
        To see your estimated payments, please select a school and an initial request for funds. If
        you are unsure, you do not need to update your school or request an amount, but you will not
        see estimated payment amounts.
      </p>

      <p v-else>
        To see your estimated payments, please select an initial request for funds. If you are
        unsure, you do not need to request an amount, but you will not see estimated payment
        amounts.
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted } from "vue";
import BaseFormField from "@/components/shared/BaseFormField.vue";
import { useStore } from "vuex";
import type { RatePlanRepaymentVM, RatePlanVM } from "@/models/loanConfiguration";
import type { FormSchema } from "@/models/form";

const fieldRef = ref<InstanceType<typeof BaseFormField>>();

const store = useStore();

const props = defineProps<{
  ratePlan: RatePlanVM;
  option: RatePlanRepaymentVM;
  schoolSelected?: boolean;
  requestedAmount?: number;
}>();

const emit = defineEmits<{
  (e: "update:model-value", value: RatePlanRepaymentVM): void;
}>();

const loanConfigurationModule = computed(() => store.state.loanConfigurationModule);

const isSelected = computed<boolean>(() => {
  return (
    loanConfigurationModule.value?.selectedRatePlan?.id === props.ratePlan?.id &&
    loanConfigurationModule.value?.selectedRepaymentOption?.repaymentTypeId ===
      props.option?.repaymentTypeId
  );
});

const selectedPaymentOption = ref<number>(0);

const schema = ref<FormSchema>({
  paymentOption: {
    name: "paymentOption",
    type: "radio",
    required: true,
    hideLabel: true,
  },
});

watch(
  () => loanConfigurationModule.value.selectedRepaymentOption?.repaymentTypeId,
  () => {
    selectedPaymentOption.value = loanConfigurationModule.value.selectedRepaymentOption;
  },
);

onMounted(() => {
  if (
    fieldRef.value &&
    fieldRef.value.radioInput &&
    (fieldRef.value.radioInput[0] as HTMLInputElement) &&
    isSelected.value
  ) {
    (fieldRef.value.radioInput[0] as HTMLInputElement).click();
  }
});
</script>

<style scoped src="@/styles/loan-options.scss" lang="scss"></style>
